import React, { useEffect, useState, Component } from 'react';
import { EyeIcon, Loader2 } from 'lucide-react';
import { FormSection, TextField, TextAreaField, FileUploadField, SelectField } from '../FormComponents';
import { DataTable } from '../DataTable';
import { StatusBadge } from '../StatusBadge';
import { SupplierViewModal } from './SupplierViewModal';
import { RFPSupplier } from '../../types';
const mockRFQSuppliers: RFPSupplier[] = [{
  id: '1',
  supplierId: 'SUP-001',
  supplierName: 'TechCorp Solutions',
  invitationDate: '2023-10-20',
  submissionStatus: 'Submitted',
  technicalScore: 85,
  financialScore: 90,
  totalScore: 87.5,
  responseStatus: 'Evaluated',
  documents: ['quotation.pdf', 'technical_specs.pdf'],
  quotedAmount: 45000,
  deliveryTimeline: '4 Weeks'
}, {
  id: '2',
  supplierId: 'SUP-002',
  supplierName: 'Global Logistics Inc',
  invitationDate: '2023-10-20',
  submissionStatus: 'Pending',
  responseStatus: 'Pending',
  documents: []
}];
interface RFQPanelProps {
  onStatusChange?: (status: string) => void;
}
export function RFQPanel({
  onStatusChange
}: RFQPanelProps) {
  const [formData, setFormData] = useState({
    submissionDeadline: '',
    evaluationCriteria: '',
    selectedSupplier: '',
    remarks: '',
    status: 'Draft'
  });
  const [suppliers, setSuppliers] = useState<RFPSupplier[]>(mockRFQSuppliers);
  const [selectedSupplierForModal, setSelectedSupplierForModal] = useState<RFPSupplier | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isReadOnly = formData.status !== 'Draft';
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange(formData.status);
    }
  }, [formData.status, onStatusChange]);
  const columns = [{
    key: 'supplierName',
    label: 'Supplier Name'
  }, {
    key: 'invitationDate',
    label: 'Invitation Date'
  }, {
    key: 'submissionStatus',
    label: 'Submission',
    render: (item: RFPSupplier) => <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.submissionStatus === 'Submitted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-414">
          {item.submissionStatus}
        </span>
  }, {
    key: 'technicalScore',
    label: 'Tech Score',
    render: (item: RFPSupplier) => <input type="number" min="0" max="100" className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 text-center disabled:bg-gray-100" value={item.technicalScore ?? ''} disabled={formData.status !== 'Published'} onChange={e => {
      const val = e.target.value === '' ? undefined : Number(e.target.value);
      setSuppliers(prev => prev.map(s => s.id === item.id ? {
        ...s,
        technicalScore: val,
        totalScore: val !== undefined && s.financialScore !== undefined ? (val + s.financialScore) / 2 : undefined
      } : s));
    }} placeholder="0-100" data-id="element-415" />
  }, {
    key: 'financialScore',
    label: 'Fin Score',
    render: (item: RFPSupplier) => <input type="number" min="0" max="100" className="block w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-1.5 text-center disabled:bg-gray-100" value={item.financialScore ?? ''} disabled={formData.status !== 'Published'} onChange={e => {
      const val = e.target.value === '' ? undefined : Number(e.target.value);
      setSuppliers(prev => prev.map(s => s.id === item.id ? {
        ...s,
        financialScore: val,
        totalScore: s.technicalScore !== undefined && val !== undefined ? (s.technicalScore + val) / 2 : undefined
      } : s));
    }} placeholder="0-100" data-id="element-416" />
  }, {
    key: 'totalScore',
    label: 'Total Score',
    render: (item: RFPSupplier) => <span className="font-medium text-indigo-600" data-id="element-417">
          {item.totalScore != null ? item.totalScore.toFixed(1) : '-'}
        </span>
  }, {
    key: 'responseStatus',
    label: 'Status',
    render: (item: RFPSupplier) => <StatusBadge status={item.responseStatus} data-id="element-418" />
  }];
  const handleViewSupplier = (supplier: RFPSupplier) => {
    setSelectedSupplierForModal(supplier);
    setIsModalOpen(true);
  };
  const handleSaveScores = (id: string, techScore: number, finScore: number) => {
    setSuppliers(prev => prev.map(s => {
      if (s.id === id) {
        return {
          ...s,
          technicalScore: techScore,
          financialScore: finScore,
          totalScore: (techScore + finScore) / 2,
          responseStatus: 'Evaluated'
        };
      }
      return s;
    }));
  };
  const handleSubmit = () => {
    setIsSubmitting(true);
    setFormData({
      ...formData,
      status: 'Pending'
    });
    // Simulate approval process
    setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        status: 'Approved'
      }));
      setIsSubmitting(false);
    }, 1500);
  };
  const handlePublish = () => {
    setFormData({
      ...formData,
      status: 'Published'
    });
  };
  return <div className="space-y-6" data-id="element-419">
      <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-420">
        <FormSection title="Request for Quotation (RFQ) Details" data-id="element-421">
          <TextField label="RFQ Submission Deadline" type="date" value={formData.submissionDeadline} onChange={e => setFormData({
          ...formData,
          submissionDeadline: e.target.value
        })} required disabled={isReadOnly} data-id="element-422" />
          <SelectField label="Select Supplier" value={formData.selectedSupplier} onChange={e => setFormData({
          ...formData,
          selectedSupplier: e.target.value
        })} options={[{
          value: '',
          label: 'Select from Supplier List'
        }, {
          value: 'SUP-001',
          label: 'TechCorp Solutions'
        }, {
          value: 'SUP-002',
          label: 'Global Logistics Inc'
        }, {
          value: 'SUP-003',
          label: 'BuildIt Construction'
        }]} required disabled={isReadOnly} data-id="element-423" />
          <div className="md:col-span-2" data-id="element-424">
            <TextAreaField label="RFQ Evaluation Criteria" value={formData.evaluationCriteria} onChange={e => setFormData({
            ...formData,
            evaluationCriteria: e.target.value
          })} required rows={3} placeholder="e.g., price, delivery timeline, compliance with specifications, warranty terms" disabled={isReadOnly} data-id="element-425" />
          </div>
          <FileUploadField label="RFQ Attachment" disabled={isReadOnly} data-id="element-426" />
          <div className="md:col-span-2" data-id="element-427">
            <TextAreaField label="Remarks" value={formData.remarks} onChange={e => setFormData({
            ...formData,
            remarks: e.target.value
          })} rows={2} disabled={isReadOnly} data-id="element-428" />
          </div>
        </FormSection>

        <div className="mt-8" data-id="element-429">
          <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-430">
            Selected Suppliers & Evaluation
          </h3>
          <div className="h-64" data-id="element-431">
            <DataTable columns={columns} data={suppliers} keyExtractor={item => item.id} actions={item => <div className="flex items-center justify-end space-x-3" data-id="element-433">
                  <button type="button" onClick={() => handleViewSupplier(item)} className="text-indigo-600 hover:text-indigo-900 flex items-center" data-id="element-434">
                    <EyeIcon className="h-4 w-4 mr-1" data-id="element-435" /> View
                  </button>
                </div>} data-id="element-432" />
          </div>
        </div>

        <div className="mt-8 pt-5 border-t border-gray-200 flex items-center justify-between" data-id="element-436">
          <div className="flex items-center" data-id="element-437">
            <span className="text-sm font-medium text-gray-500 mr-2" data-id="element-438">
              Status:
            </span>
            <StatusBadge status={formData.status} data-id="element-439" />
          </div>
          <div className="flex space-x-3" data-id="element-440">
            {formData.status === 'Draft' && <>
                <button type="button" onClick={() => setFormData({
              ...formData,
              status: 'Draft'
            })} className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-441">
                  Save as Draft
                </button>
                <button type="button" onClick={handleSubmit} disabled={isSubmitting} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50" data-id="element-442">
                  {isSubmitting && <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" data-id="element-443" />}
                  Submit for Approval
                </button>
              </>}
            {formData.status === 'Approved' && <button type="button" onClick={handlePublish} className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700" data-id="element-444">
                Publish RFQ
              </button>}
          </div>
        </div>
      </div>

      <SupplierViewModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} supplier={selectedSupplierForModal} type="RFQ" onSaveScores={handleSaveScores} data-id="element-445" />
    </div>;
}